// Central mock data for the CM multi-community overview ("My Communities").
// FE-first / phase 1 — NOT wired to the backend. Bilingual names ({ en, he })
// are resolved to the active language in the container via a small `loc()`
// helper so the demo reads fully in Hebrew.
//
// One entry per community the CM manages. The container computes the
// portfolio aggregate strip + the 12-month multi-line income graph by reading
// across these. `monthlyGoal` / `goalCollected` / `activeProjects` /
// `piggybankAvailable` mirror the Community Piggybank feature. `income12mo` is
// the last-12-months transaction volume that feeds the growth graph. To change
// demo content, edit the values here only.

// Last 12 calendar months, ending in the current month (Jul 2026). The year is
// baked into each label so the timeline direction is unambiguous.
export const months12 = [
  { en: "Aug '25", he: "אוג׳ 25" },
  { en: "Sep '25", he: "ספט׳ 25" },
  { en: "Oct '25", he: "אוק׳ 25" },
  { en: "Nov '25", he: "נוב׳ 25" },
  { en: "Dec '25", he: "דצמ׳ 25" },
  { en: "Jan '26", he: "ינו׳ 26" },
  { en: "Feb '26", he: "פבר׳ 26" },
  { en: "Mar '26", he: "מרץ 26" },
  { en: "Apr '26", he: "אפר׳ 26" },
  { en: "May '26", he: "מאי 26" },
  { en: "Jun '26", he: "יוני 26" },
  { en: "Jul '26", he: "יולי 26" },
];

const MEMBER_AVATARS = ["/avataar.png", "/usravatar.png", "/profile.png", "/people.png"];
const SUPPLIER_AVATARS = ["/mysupplier.png", "/supplier-icon.png", "/people.png", "/avataar.png"];

export const communitiesOverviewMock = {
  currencySymbol: "₪",

  communities: [
    {
      id: 1,
      name: { en: "Neve Sha'anan Towers", he: "מגדלי נווה שאנן" },
      image: "/mysupplier.png",
      color: "#7367F0",
      status: "active",
      members: { current: 128, cap: 150 },
      suppliers: 34,
      memberAvatars: MEMBER_AVATARS,
      supplierAvatars: SUPPLIER_AVATARS,
      revenueThisMonth: 32100,
      earningsThisMonth: 1605,
      monthlyGoal: 5000,
      goalCollected: 4200,
      activeProjects: 3,
      piggybankAvailable: 12400,
      needs: { joinRequests: 4, supplierOffers: 2 },
      setupPercent: 100,
      income12mo: [21000, 22500, 20000, 24000, 26000, 23500, 27000, 28500, 26500, 30000, 29000, 32100],
    },
    {
      id: 2,
      name: { en: "Park HaYarkon Residences", he: "מעונות פארק הירקון" },
      image: "/supplier-icon.png",
      color: "#28C76F",
      status: "active",
      members: { current: 96, cap: 120 },
      suppliers: 28,
      memberAvatars: MEMBER_AVATARS,
      supplierAvatars: SUPPLIER_AVATARS,
      revenueThisMonth: 24800,
      earningsThisMonth: 1240,
      monthlyGoal: 4000,
      goalCollected: 4000,
      activeProjects: 2,
      piggybankAvailable: 8600,
      needs: { joinRequests: 0, supplierOffers: 3 },
      setupPercent: 100,
      income12mo: [16000, 17500, 15000, 18000, 20000, 19000, 21000, 22500, 21000, 23500, 22000, 24800],
    },
    {
      id: 3,
      name: { en: "Rothschild 12", he: "רוטשילד 12" },
      image: "/people.png",
      color: "#FF9F43",
      status: "active",
      members: { current: 64, cap: 80 },
      suppliers: 19,
      memberAvatars: MEMBER_AVATARS,
      supplierAvatars: SUPPLIER_AVATARS,
      revenueThisMonth: 18600,
      earningsThisMonth: 930,
      monthlyGoal: 3000,
      goalCollected: 1650,
      activeProjects: 1,
      piggybankAvailable: 3200,
      needs: { joinRequests: 7, supplierOffers: 0 },
      setupPercent: 100,
      income12mo: [10000, 11000, 9500, 12000, 13500, 12500, 14000, 15500, 14500, 16500, 17000, 18600],
    },
    {
      id: 4,
      name: { en: "Herzliya Pituach", he: "הרצליה פיתוח" },
      image: "/avataar.png",
      color: "#00CFE8",
      status: "active",
      members: { current: 41, cap: 100 },
      suppliers: 12,
      memberAvatars: MEMBER_AVATARS,
      supplierAvatars: SUPPLIER_AVATARS,
      revenueThisMonth: 9400,
      earningsThisMonth: 470,
      monthlyGoal: 2500,
      goalCollected: 900,
      activeProjects: 1,
      piggybankAvailable: 1500,
      needs: { joinRequests: 2, supplierOffers: 1 },
      setupPercent: 80,
      income12mo: [3000, 3500, 4000, 4500, 5500, 6000, 6500, 7000, 7500, 8200, 8800, 9400],
    },
    {
      id: 5,
      name: { en: "Ramat Aviv Green", he: "רמת אביב הירוקה" },
      image: "/usravatar.png",
      color: "#EA5455",
      status: "paused",
      members: { current: 52, cap: 90 },
      suppliers: 15,
      memberAvatars: MEMBER_AVATARS,
      supplierAvatars: SUPPLIER_AVATARS,
      revenueThisMonth: 0,
      earningsThisMonth: 0,
      monthlyGoal: 0,
      goalCollected: 0,
      activeProjects: 0,
      piggybankAvailable: 0,
      needs: { joinRequests: 0, supplierOffers: 0 },
      setupPercent: 100,
      income12mo: [8000, 7500, 7000, 6500, 6000, 5000, 4000, 3000, 2000, 1000, 500, 0],
    },
    {
      id: 6,
      name: { en: "Old North TLV", he: "הצפון הישן תל אביב" },
      image: "/profile.png",
      color: "#A66DD4",
      status: "active",
      members: { current: 88, cap: 110 },
      suppliers: 22,
      memberAvatars: MEMBER_AVATARS,
      supplierAvatars: SUPPLIER_AVATARS,
      revenueThisMonth: 15200,
      earningsThisMonth: 760,
      monthlyGoal: 3500,
      goalCollected: 2800,
      activeProjects: 2,
      piggybankAvailable: 5400,
      needs: { joinRequests: 1, supplierOffers: 5 },
      setupPercent: 100,
      income12mo: [9000, 9500, 10000, 11000, 12000, 11500, 13000, 13500, 13000, 14500, 14000, 15200],
    },
    {
      id: 7,
      name: { en: "Sea View Apartments", he: "דירות נוף לים" },
      image: "/mysupplier.png",
      color: "#F1B44C",
      status: "draft",
      members: { current: 6, cap: 60 },
      suppliers: 2,
      memberAvatars: MEMBER_AVATARS,
      supplierAvatars: SUPPLIER_AVATARS,
      revenueThisMonth: 0,
      earningsThisMonth: 0,
      monthlyGoal: 0,
      goalCollected: 0,
      activeProjects: 0,
      piggybankAvailable: 0,
      needs: { joinRequests: 0, supplierOffers: 0 },
      setupPercent: 35,
      income12mo: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    },
  ],
};
